import React, { type FC, type ReactNode, type PropsWithChildren } from 'react';

import styled from '@emotion/styled';
import { variant } from '@lucid/theme';
import { AccordionItemState, AccordionItem as ReactAccordionItem } from 'react-accessible-accordion';

import type { Lucid } from '../../types/types';
import AccordionPanel, { type AccordionPanelProps } from './accordion-panel';

export interface AccordionItemProps {
  /** Allows the component to get additional styles. */
  className?: string;
  /** The children to be rendered with the Accordion.Item. */
  children?: ReactNode;
  /** The available variants for the `Accordion.Item` component. */
  variant?: Lucid.Theme.Component.Accordion.Item.VariantKeys;
}

const StyledAccordionItem = styled(ReactAccordionItem)<AccordionItemProps>(
  variant({
    scale: 'accordion.item',
  }),
  {
    label: 'item',
    display: 'flex',
    flex: '0 1 100%',
    flexWrap: 'wrap',
  },
);

const AccordionItem: FC<PropsWithChildren<AccordionItemProps>> = ({
  className,
  variant = 'default',
  children,
  ...rest
}) => (
  <StyledAccordionItem {...rest} className={className} variant={variant}>
    <AccordionItemState>
      {state =>
        React.Children.map(children, child => {
          if (React.isValidElement(child) && child.type === AccordionPanel)
            return React.cloneElement(child as React.ReactElement<AccordionPanelProps>, { state: state });

          return child;
        })
      }
    </AccordionItemState>
  </StyledAccordionItem>
);

export { AccordionItem };
export default AccordionItem;
